import { createSlice } from "@reduxjs/toolkit";
import {
  getPayments,
  addNewPayment,
  updatePayment,
  deletePayment,
  getPaymentById,
  updatePaymentBulk,
} from "./thunk";

export const initialState = {
  payments: [],
  error: {},
  selectedPayment: null,
  isPaymentSuccess: false,
  isPaymentCreated: false,
};

const paymentsSlice = createSlice({
  name: "PaymentsSlice",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch payments
    builder.addCase(getPayments.fulfilled, (state, action) => {
      state.payments = action.payload; // Assuming `action.payload` contains payment data
      state.isPaymentSuccess = true;
      state.error = null;
    });

    builder.addCase(getPayments.rejected, (state, action) => {
      state.error = action.payload?.error || "Failed to fetch payments";
      state.isPaymentSuccess = false;
    });
    // Fetch a single payment by ID
    builder.addCase(getPaymentById.fulfilled, (state, action) => {
      state.selectedPayment = action.payload; // Set the selected payment
      state.error = null;
    });

    builder.addCase(getPaymentById.rejected, (state, action) => {
      state.error = action.payload?.error || "Failed to fetch payment by ID";
      state.selectedPayment = null; // Clear the selected payment on failure
    });

    // Add new payment
    builder.addCase(addNewPayment.fulfilled, (state, action) => {
      state.payments.push(action.payload); // Add the new payment to the list
      state.isPaymentCreated = true;
      state.error = null;
    });

    builder.addCase(addNewPayment.rejected, (state, action) => {
      state.error = action.payload || "Failed to add payment";
      state.isPaymentCreated = false;
    });

    // Update payment
    builder.addCase(updatePayment.fulfilled, (state, action) => {
      state.payments = state.payments.map((payment) =>
        payment.id === action.payload.id ? { ...payment, ...action.payload } : payment
      );
      state.error = null;
    });
    

    builder.addCase(updatePayment.rejected, (state, action) => {
      state.error = action.payload?.error || "Failed to update payment";
    });

    // Update payment bulk
    builder.addCase(updatePaymentBulk.fulfilled, (state, action) => {
      if (state.selectedContractFull && action.payload?.success && action.payload?.data) {
          const { payments, updated_units, contract_summary } = action.payload.data;
          
          if (updated_units && Array.isArray(updated_units)) {
          updated_units.forEach(updatedUnit => {
              const unitIndex = state.selectedContractFull.units.findIndex(u => u.id === updatedUnit.id);
              if (unitIndex !== -1) {
              if (updatedUnit.settlement_price) {
                  state.selectedContractFull.units[unitIndex].settlement_price = {
                  ...state.selectedContractFull.units[unitIndex].settlement_price,
                  ...updatedUnit.settlement_price
                  };
              }
              
              const unitPayments = payments.filter(p => p.unit_id === updatedUnit.id);
              if (unitPayments.length > 0) {
                  state.selectedContractFull.units[unitIndex].settlement_price.payments = unitPayments.map(payment => ({
                  id: payment.id,
                  type: payment.type,
                  amount: payment.amount,
                  cashed: payment.cashed,
                  booking_date: payment.booking_date,
                  cashed_date: payment.cashed_date,
                  vat_percentage: payment.vat_percentage,
                  tax: payment.tax
                  }));
              }
              }
          });
          }
          
          if (contract_summary) {
          if (contract_summary.total_cashed !== undefined) {
              state.selectedContractFull.total_cashed = contract_summary.total_cashed;
          }
          if (contract_summary.total_contract_value !== undefined) {
              state.selectedContractFull.total_contract_value = contract_summary.total_contract_value;
          }
          if (contract_summary.remaining_balance !== undefined) {
              state.selectedContractFull.remaining_balance = contract_summary.remaining_balance;
          }
          }
      }
      else if (state.selectedContractFull && Array.isArray(action.payload)) {
          const updatedPayments = action.payload;
          
          const paymentsBySettlementPrice = {};
          updatedPayments.forEach(payment => {
          const settlementPriceId = payment.settlement_price;
          if (settlementPriceId) {
              if (!paymentsBySettlementPrice[settlementPriceId]) {
              paymentsBySettlementPrice[settlementPriceId] = [];
              }
              paymentsBySettlementPrice[settlementPriceId].push(payment);
          }
          });

          state.selectedContractFull.units = state.selectedContractFull.units.map(unit => {
          if (unit.settlement_price && paymentsBySettlementPrice[unit.settlement_price.id]) {
              const newPayments = paymentsBySettlementPrice[unit.settlement_price.id];
              
              unit.settlement_price.payments = newPayments.map(payment => ({
              id: payment.id,
              type: payment.type,
              amount: payment.amount,
              cashed: payment.cashed,
              booking_date: payment.booking_date,
              cashed_date: payment.cashed_date,
              vat_percentage: payment.vat_percentage,
              tax: payment.tax
              }));

              unit.settlement_price.total_cashed = newPayments.reduce((sum, payment) => {
              const cashedAmount = payment.cashed !== null && parseFloat(payment.cashed || 0) > 0 
                  ? parseFloat(payment.cashed || 0) 
                  : 0;
              return sum + cashedAmount;
              }, 0);
          }
          return unit;
          });

          state.selectedContractFull.total_cashed = state.selectedContractFull.units.reduce((total, unit) => {
          return total + (unit.settlement_price?.total_cashed || 0);
          }, 0);
      }
    });

    builder.addCase(updatePaymentBulk.rejected, (state, action) => {
      state.error = action.payload?.error || "Failed to update payment";
    });

    // Delete payment
    builder.addCase(deletePayment.fulfilled, (state, action) => {
      state.payments = state.payments.filter(
        (payment) => payment.id !== action.payload.paymentId
      );
      state.error = null;
    });

    builder.addCase(deletePayment.rejected, (state, action) => {
      state.error = action.payload?.error || "Failed to delete payment";
    });
  },
});

export default paymentsSlice.reducer;
